/**
 * AppLabelsStorage — local storage of AppLabels content records (UI text per language).
 *
 * AppLabels arrives as a ContentType='AppLabels' record (one per language) in the content
 * bundle. content_body is base64-encoded JSON (PascalCase on the wire):
 *   { "LanguageTypeId": "2", "Labels": [ { "Key": "nav.back", "Text": "Atrás" }, ... ] }
 *
 * Storage structure (same shape as ActionDefStorage):
 *   Key:   app_labels_{languageTypeId}   e.g. app_labels_2
 *   Value: JSON-serialised AppLabelsDto (camelCase, labels as a key -> text map)
 *   Index: app_labels_index              array of stored languageTypeIds
 */

import type { ContentRecord } from '../stores/contentStore';

export interface AppLabelsStorageInterface {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

export interface AppLabelsDto {
  languageTypeId: string;
  /** label key -> text */
  labels: Record<string, string>;
  /** ISO timestamp set when the record was stored locally */
  downloadedAt: string;
}

const decodeBase64Utf8 = (b64: string): string => {
  const NodeBuffer = (globalThis as any).Buffer;
  if (NodeBuffer) {
    return NodeBuffer.from(b64, 'base64').toString('utf-8');
  }
  const binary = atob(b64);
  if (typeof (globalThis as any).TextDecoder === 'function') {
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new (globalThis as any).TextDecoder('utf-8').decode(bytes);
  }
  return binary;
};

/**
 * Parses a decoded AppLabels body into a key -> text map. Accepts the PascalCase wire form
 * ({ LanguageTypeId, Labels: [{ Key, Text }] }), its camelCase form, and a flat
 * { Labels: { key: text } } map.
 */
export const parseAppLabelsBody = (
  body: any,
  fallbackLanguageTypeId = ''
): { languageTypeId: string; labels: Record<string, string> } | null => {
  if (!body || typeof body !== 'object') {
    return null;
  }
  // The body's own LanguageTypeId wins; otherwise use the language of the content record it
  // came from (content row / record id), so a body that omits it still unpacks.
  const languageTypeId = String(body.LanguageTypeId ?? body.languageTypeId ?? fallbackLanguageTypeId ?? '').trim();
  const rawLabels = body.Labels ?? body.labels;
  if (!languageTypeId || !rawLabels) {
    return null;
  }

  const labels: Record<string, string> = {};
  if (Array.isArray(rawLabels)) {
    rawLabels.forEach((entry: any) => {
      const key = entry?.Key ?? entry?.key;
      const text = entry?.Text ?? entry?.text;
      if (typeof key === 'string' && key && typeof text === 'string') {
        labels[key] = text;
      }
    });
  } else if (typeof rawLabels === 'object') {
    Object.entries(rawLabels).forEach(([key, text]) => {
      if (typeof text === 'string') {
        labels[key] = text;
      }
    });
  }
  return { languageTypeId, labels };
};

export class AppLabelsStorage {
  private readonly keyPrefix = 'app_labels_';
  private readonly indexKey = 'app_labels_index';

  constructor(private storage: AppLabelsStorageInterface) {}

  private storageKey(languageTypeId: string): string {
    return `${this.keyPrefix}${languageTypeId}`;
  }

  private async getIndex(): Promise<string[]> {
    try {
      const raw = await this.storage.getItem(this.indexKey);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  private async addToIndex(languageTypeId: string): Promise<void> {
    try {
      const ids = await this.getIndex();
      if (!ids.includes(languageTypeId)) {
        ids.push(languageTypeId);
        await this.storage.setItem(this.indexKey, JSON.stringify(ids));
      }
    } catch { /* non-fatal */ }
  }

  async saveAppLabels(dto: AppLabelsDto): Promise<boolean> {
    try {
      const record: AppLabelsDto = { ...dto, downloadedAt: dto.downloadedAt || new Date().toISOString() };
      await this.storage.setItem(this.storageKey(record.languageTypeId), JSON.stringify(record));
      await this.addToIndex(record.languageTypeId);
      return true;
    } catch (err) {
      console.error('❌ AppLabelsStorage.saveAppLabels failed:', err);
      return false;
    }
  }

  async getAppLabels(languageTypeId: string): Promise<AppLabelsDto | null> {
    try {
      const raw = await this.storage.getItem(this.storageKey(languageTypeId));
      return raw ? (JSON.parse(raw) as AppLabelsDto) : null;
    } catch (err) {
      console.error('❌ AppLabelsStorage.getAppLabels failed:', languageTypeId, err);
      return null;
    }
  }

  /** LanguageTypeIds that have a stored AppLabels record. */
  async getAllLanguages(): Promise<string[]> {
    return this.getIndex();
  }

  /**
   * Unpack a ContentStore record of type "AppLabels" (tableData with a content_body column
   * holding the base64 JSON) and persist it. Returns the stored dto, or null on failure.
   */
  async unpackFromContentRecord(record: ContentRecord): Promise<AppLabelsDto | null> {
    try {
      const recordId = (record as any).id;
      const tableData = (record as any).tableData;
      const columns: string[] | undefined = tableData?.Data?.Columns;
      const rows: any[][] | undefined = tableData?.Data?.Rows;
      if (!columns || !rows?.length) {
        console.warn('🌐[labels] AppLabels unpack: no Data.Columns/Rows', { recordId, hasData: !!tableData?.Data, columns, rowCount: rows?.length });
        return null;
      }
      const lowerColumns = columns.map(c => String(c).toLowerCase());
      const bodyIdx = lowerColumns.indexOf('content_body');
      const encodedBody: string | undefined = bodyIdx >= 0 ? rows[0][bodyIdx] : undefined;
      if (!encodedBody) {
        console.warn('🌐[labels] AppLabels unpack: no content_body', { recordId, columns });
        return null;
      }

      // Language fallback for a body without its own LanguageTypeId: the content row's
      // content_languagetype_id column, else the trailing segment of the record id
      // ("AppLabels_<contentId>_<languageTypeId>").
      const langIdx = lowerColumns.indexOf('content_languagetype_id');
      const rowLanguage = langIdx >= 0 ? String(rows[0][langIdx] ?? '').trim() : '';
      const idLanguage = typeof recordId === 'string' && recordId.includes('_') ? recordId.split('_').pop() ?? '' : '';

      // Normally base64 JSON; tolerate a raw JSON body too.
      let body: any;
      try {
        body = JSON.parse(decodeBase64Utf8(encodedBody));
      } catch {
        body = JSON.parse(encodedBody);
      }

      const parsed = parseAppLabelsBody(body, rowLanguage || idLanguage);
      if (!parsed) {
        console.warn('🌐[labels] AppLabels unpack: body not recognised', {
          recordId,
          bodyKeys: body && typeof body === 'object' ? Object.keys(body) : typeof body,
          rowLanguage,
          idLanguage,
        });
        return null;
      }
      const dto: AppLabelsDto = { ...parsed, downloadedAt: new Date().toISOString() };
      return (await this.saveAppLabels(dto)) ? dto : null;
    } catch (err) {
      console.error('❌ AppLabelsStorage.unpackFromContentRecord failed:', err);
      return null;
    }
  }
}
